import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";

let initStarted = false;
let initPromise: Promise<void> | null = null;

/**
 * Initialize Sentry by fetching the DSN from the `get-sentry-config`
 * edge function. The DSN is stored as a Cloud secret (`SENTRY_DSN`)
 * rather than in the Vite bundle, so we hydrate it at runtime.
 *
 * Safe to call multiple times — only the first call performs the fetch.
 * If the DSN is not configured, Sentry stays disabled (no errors thrown).
 */
export function initSentry(): Promise<void> {
  if (initStarted) return initPromise!;
  initStarted = true;

  initPromise = (async () => {
    try {
      const { data, error } = await supabase.functions.invoke("get-sentry-config");
      if (error || !data?.dsn) {
        if (import.meta.env.DEV) {
          console.info("[sentry] disabled (no DSN configured)");
        }
        return;
      }

      Sentry.init({
        dsn: data.dsn as string,
        environment: (data.environment as string) || import.meta.env.MODE,
        // Build-time release tag wins (matches uploaded source maps).
        // Fall back to the value returned by the edge config, if any.
        release:
          (typeof __SENTRY_RELEASE__ !== "undefined" && __SENTRY_RELEASE__) ||
          (data.release as string) ||
          undefined,
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            // HIPAA: never capture PHI in replays.
            maskAllText: true,
            blockAllMedia: true,
          }),
          Sentry.httpClientIntegration(),
        ],
        beforeSend(event) {
          // Strip Authorization headers if present.
          if (event.request?.headers) {
            const headers = { ...event.request.headers };
            delete headers["Authorization"];
            delete headers["authorization"];
            event.request.headers = headers;
          }
          // Strip query strings on PHI-adjacent routes.
          const url = event.request?.url;
          if (url && /\/(leads|quick-intake)/.test(url)) {
            event.request!.url = url.split("?")[0];
          }
          return event;
        },
      });
    } catch (e) {
      // Never let Sentry init crash the app.
      console.warn("[sentry] init failed:", e);
    }
  })();

  return initPromise;
}

export function setSentryUser(user: { id: string; agencyId?: string | null; role?: string | null }) {
  Sentry.setUser({ id: user.id });
  Sentry.setTag("agency_id", user.agencyId ?? "none");
  if (user.role) Sentry.setTag("team_role", user.role);
}

export function clearSentryUser() {
  Sentry.setUser(null);
  Sentry.setTag("agency_id", "none");
  Sentry.setTag("team_role", "none");
}

export { Sentry };